/**
 * Sidebar V2 - Ny arkitektur med System
 * 
 * Visar bara system i menyn. Klick på system med moduler
 * öppnar en landningssida med modulkort.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useAuthActions } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import { ROUTES } from '@/constants/routes';
import { withBackTo } from '@/utils/navigationBack';
import { resolveWorkshopModuleRoute } from '@/utils/workshopModuleRoutes';
import { tModule } from '@/utils/moduleLabels';
import api from '@/services/api/client';
import * as HeroIconsOutline from '@heroicons/react/24/outline';
import * as HeroIconsSolid from '@heroicons/react/24/solid';
import ProfileModal from '@/components/profile/ProfileModal';
import { useTheme } from 'next-themes';
import { getConfig, type Config } from '@/services/api/configService';
import { DeviaqDefaultLogo } from '@/components/brand/DeviaqMark';

// =====================================================
// TYPES
// =====================================================

interface MenuModule {
  id: string;
  name: string;
  displayName: string;
  icon?: string;
  route?: string;
  sortOrder: number;
  isEnabled: boolean;
  isDefault: boolean;
}

interface MenuSystem {
  id: string;
  name: string;
  displayName: string;
  icon?: string;
  route?: string;
  color?: string;
  sortOrder: number;
  isEnabled: boolean;
  modules: MenuModule[];
}

interface UserMenuResponse {
  systems: MenuSystem[];
}

// =====================================================
// ROUTE MATCHING
// =====================================================

/** Matchar menyroute som prefix med path-gräns (route "/" matchar bara dashboard-roten). */
function matchesMenuRoute(pathname: string, route: string): boolean {
  if (!route) return false;
  const path = pathname.replace(/\/+$/, '') || '/';
  const base = route.replace(/\/+$/, '') || '/';
  if (base === '/') {
    return path === '/';
  }
  return path === base || path.startsWith(`${base}/`);
}

function getSystemMatchScore(system: MenuSystem, pathname: string): number {
  if (pathname === `/system/${system.name}`) {
    return 1000 + system.name.length;
  }
  if (system.route && matchesMenuRoute(pathname, system.route)) {
    return system.route.replace(/\/+$/, '').length || 1;
  }
  let bestModuleScore = 0;
  for (const module of system.modules) {
    const moduleRoute = resolveWorkshopModuleRoute(system.name, module.name, module.route);
    if (moduleRoute && matchesMenuRoute(pathname, moduleRoute)) {
      const score = moduleRoute.replace(/\/+$/, '').length;
      if (score > bestModuleScore) bestModuleScore = score;
    }
  }
  return bestModuleScore;
}

export interface SidebarV2Props {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

// =====================================================
// SIDEBAR COMPONENT
// =====================================================

const SidebarV2 = ({ collapsed = false, onToggleCollapsed }: SidebarV2Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { user } = useAuth();
  const { logOutUser } = useAuthActions();
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // State
  const [menu, setMenu] = useState<MenuSystem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [config, setConfig] = useState<Config | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);

  // Färgpalett för ikoner - unika färger i MÖRKT läge, grå i LJUST läge
  const iconColors: Record<string, string> = {
    'ChartBarIcon': '#a78bfa',      // purple - Dashboard
    'MapIcon': '#22d3ee',           // cyan - Realtidskarta
    'TruckIcon': '#34d399',         // emerald - Deque
    'ClipboardDocumentListIcon': '#fbbf24', // amber - Arbetsorder
    'CalendarIcon': '#f472b6',      // pink - Booking
    'WrenchIcon': '#f87171',        // red - Verkstad
    'KeyIcon': '#facc15',           // yellow - Nyckelhantering
    'SunIcon': '#fb923c',           // orange - Temperaturövervakning
    'DocumentTextIcon': '#60a5fa',  // blue - Rapporter
    'ChartPieIcon': '#a78bfa',      // purple - Analys
    'UsersIcon': '#2dd4bf',         // teal - CRM
    'CogIcon': '#9ca3af',           // gray - Administration
    'ServerIcon': '#818cf8',        // indigo - SysAdmin
    'MapPinIcon': '#fb7185',        // rose - Utlämningsplatser
  };

  // Icon helper - färgade i mörkt läge, grå i ljust läge
  const getIcon = (iconName: string | undefined, solid: boolean = false, _active: boolean = false): JSX.Element => {
    // Ljust läge = grå, Mörkt läge = unika färger
    const iconColor = isDark 
      ? (iconName && iconColors[iconName] ? iconColors[iconName] : '#9ca3af')
      : '#6b7280'; // Alltid grå i ljust läge
    
    // Wrappa i span för att sätta färg via CSS color (påverkar currentColor i SVG)
    const wrapIcon = (icon: JSX.Element) => (
      <span style={{ color: iconColor }} className="inline-flex">{icon}</span>
    );
    
    if (!iconName) {
      return wrapIcon(<HeroIconsOutline.QuestionMarkCircleIcon className="w-5 h-5" />);
    }
    
    const icons = solid ? HeroIconsSolid : HeroIconsOutline;
    const IconComponent = (icons as any)[iconName];
    
    if (IconComponent) {
      return wrapIcon(<IconComponent className="w-5 h-5" />);
    }
    
    return wrapIcon(<HeroIconsOutline.QuestionMarkCircleIcon className="w-5 h-5" />);
  };

  // Hämta config (kundlogotyp m.m.) när användaren byts (impersonation).
  useEffect(() => {
    setConfig(null);
    setLogoFailed(false);
    getConfig()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [user?.id]);

  // Hämta menystruktur - uppdateras när user ändras (t.ex. vid impersonation)
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<{ success: boolean; data: UserMenuResponse }>('/api/menu');
        if (response.data.success && response.data.data?.systems) {
          setMenu(response.data.data.systems);
        }
      } catch (error) {
        console.error('[SidebarV2] Error fetching menu:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMenu();
  }, [user?.id]);

  const handleSystemClick = (system: MenuSystem) => {
    // Realtidskarta öppnas alltid i nytt fönster
    if (system.name === 'realtime' && system.route) {
      window.open(system.route, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // System med direkt route (admin-sidor, crm, etc) → navigera direkt
    // Detta gäller system vars route börjar med /admin, /crm, /sysadmin, etc
    if (system.route && !system.route.startsWith('/system/')) {
      navigate(system.route, withBackTo(pathname === system.route ? ROUTES.HOME : pathname));
      return;
    }
    
    // System med moduler och /system route → gå till landningssida
    if (system.modules.length > 0) {
      navigate(`/system/${system.name}`, withBackTo(ROUTES.HOME));
      return;
    } 
    
    // System utan moduler → navigera till route
    if (system.route) {
      navigate(system.route, withBackTo(ROUTES.HOME));
    }
  };

  const activeSystemId = (() => {
    let bestId: string | null = null;
    let bestScore = 0;
    for (const system of menu) {
      const score = getSystemMatchScore(system, pathname);
      if (score > bestScore) {
        bestScore = score;
        bestId = system.id;
      }
    }
    return bestId;
  })();

  const isActive = (system: MenuSystem): boolean => system.id === activeSystemId;

  const onSignOut = () => {
    logOutUser();
    navigate(ROUTES.LOGIN);
  };

  const firstName = user?.first_name || t('sidebar.user');
  const lastName = user?.last_name || '';
  const userEmail = user?.email || '';

  return (
    <aside className="h-full flex flex-col p-2 sm:p-4">
      {/* Top Section */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Logo – kundlogotyp om konfigurerad, annars DeviaQ */}
        <div className={`flex flex-col items-center mb-4 flex-shrink-0 ${collapsed ? 'gap-1' : 'mb-6'}`}>
          {config?.organizationLogoUrl && !logoFailed ? (
            <>
              <img
                src={config.organizationLogoUrl}
                alt={config.organizationName || ''}
                className={`object-contain ${collapsed ? 'max-h-10 w-10' : 'w-full max-h-16'}`}
                onError={() => setLogoFailed(true)}
              />
              {!collapsed && (
                <span className="text-sm font-semibold mt-2 text-textSecondary text-center">
                  {config.organizationName || 'DeviaQ'}
                </span>
              )}
            </>
          ) : (
            <DeviaqDefaultLogo collapsed={collapsed} />
          )}
          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="mt-2 rounded-lg p-1.5 text-textSecondary hover:bg-bgHover hover:text-textDefault focus:outline-none focus:ring-2 focus:ring-primary"
              title={collapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
            >
              {collapsed ? (
                <HeroIconsOutline.ChevronDoubleRightIcon className="h-5 w-5" aria-hidden />
              ) : (
                <HeroIconsOutline.ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden />
              )}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav data-guide="sidebar-nav" className="flex-1 overflow-y-auto min-h-0 space-y-1">
          {isLoading ? (
            <div className="text-textTertiary text-sm p-3">{t('common.loading')}</div>
          ) : menu.length === 0 ? (
            <div className="text-textTertiary text-sm p-3">{t('sidebar.noFeatures')}</div>
          ) : (
            menu.map(system => {
              const active = isActive(system);
              const label = tModule(t, system.name, 'title') || system.displayName;

              return (
                <button
                  key={system.id}
                  data-guide={`nav-${system.name}`}
                  type="button"
                  title={label}
                  className={`w-full font-normal cursor-pointer flex items-center rounded-lg transition-all duration-200 ${
                    collapsed ? 'justify-center p-2.5' : 'justify-start gap-3 p-3'
                  } ${
                    active
                      ? 'bg-BgPrimaryLight shadow-[inset_0px_0px_3px_0px_var(--bgPrimary)]'
                      : 'hover:bg-BgPrimaryLight/50'
                  }`}
                  onClick={() => handleSystemClick(system)}
                >
                  {getIcon(system.icon, active, active)}
                  {!collapsed && (
                    <>
                      <span
                        className={`flex-1 min-w-0 text-left truncate ${active ? 'text-textPrimary' : 'text-textSecondary hover:text-textPrimary'}`}
                      >
                        {label}
                      </span>
                      {system.modules.length > 0 && (
                        <HeroIconsOutline.ChevronRightIcon className="w-4 h-4 text-textTertiary shrink-0" />
                      )}
                    </>
                  )}
                </button>
              );
            })
          )}
        </nav>
      </div>

      {/* Bottom Section - User info */}
      <div data-guide="header-user-menu" className="flex flex-col items-center gap-1 mt-4 flex-shrink-0 pt-4 border-t border-border">
        <div
          className="flex flex-col items-center gap-1 cursor-pointer rounded-lg p-2 hover:bg-bgHover transition-colors"
          onClick={() => setShowProfileModal(true)}
          title={t('profile.title')}
        >
          <div
            className={`rounded-full bg-BgPrimaryLight flex items-center text-bgPrimary justify-center font-bold ${
              collapsed ? 'h-10 w-10 text-sm' : 'h-16 w-16 mb-2 text-lg'
            }`}
          >
            {firstName.charAt(0)}
            {lastName.charAt(0)}
          </div>
          {!collapsed && (
            <>
              <div className="text-center text-text-default text-sm font-semibold">
                {firstName} {lastName}
              </div>
              <div className="text-xs text-textTertiary">{userEmail}</div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onSignOut}
          title={collapsed ? t('sidebar.logout') : undefined}
          className={`text-textDanger text-sm mt-1 hover:underline inline-flex items-center justify-center ${
            collapsed ? 'p-2 rounded-lg hover:bg-bgHover' : 'p-0 h-auto'
          }`}
        >
          {collapsed ? <HeroIconsOutline.ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden /> : t('sidebar.logout')}
        </button>
      </div>

      {/* Profile Modal */}
      <ProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
    </aside>
  );
};

export default SidebarV2;
